/**
 * FIFO partiya narxlari va qoldiq summasi — ombor jadvali uchun.
 */

export type WarehouseBatchLike = {
  id?: number;
  purchasePrice?: number;
  purchase_price?: number;
  salePrice?: number;
  sale_price?: number;
  quantityRemaining?: number;
  quantity_remaining?: number;
  quantity?: number;
};

export type WarehouseRowWithBatches = {
  purchasePrice?: number;
  salePrice?: number;
  stock?: number;
  batches?: WarehouseBatchLike[] | null;
  fifoPurchasePrice?: number | null;
  fifo_purchase_price?: number | null;
  fifoSalePrice?: number | null;
  fifo_sale_price?: number | null;
  batchStockValueTotal?: number | null;
  batch_stock_value_total?: number | null;
};

function batchQty(b: WarehouseBatchLike): number {
  const q = Number(
    b.quantityRemaining ?? b.quantity_remaining ?? b.quantity ?? 0,
  );
  return Number.isFinite(q) && q > 0 ? q : 0;
}

function batchPurchase(b: WarehouseBatchLike): number {
  const p = Number(b.purchasePrice ?? b.purchase_price ?? 0);
  return Number.isFinite(p) && p > 0 ? p : 0;
}

function batchSale(b: WarehouseBatchLike): number {
  const s = Number(b.salePrice ?? b.sale_price ?? 0);
  return Number.isFinite(s) && s > 0 ? s : 0;
}

export function normalizeWarehouseBatches(
  batches: unknown,
): Array<{
  id: number;
  purchasePrice: number;
  salePrice: number;
  quantityRemaining: number;
}> {
  if (!Array.isArray(batches)) return [];
  return batches
    .map((b) => {
      if (!b || typeof b !== 'object') return null;
      const bb = b as WarehouseBatchLike;
      const qty = batchQty(bb);
      if (qty <= 0) return null;
      return {
        id: Number(bb.id ?? 0),
        purchasePrice: batchPurchase(bb),
        salePrice: batchSale(bb),
        quantityRemaining: qty,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x != null);
}

/** Eng qadimgi aktiv partiya (FIFO). */
export function getFifoBatch(
  batches: unknown,
): { purchasePrice: number; salePrice: number } | null {
  const list = normalizeWarehouseBatches(batches);
  if (list.length === 0) return null;
  const first = list[0]!;
  return {
    purchasePrice: first.purchasePrice,
    salePrice: first.salePrice,
  };
}

export function resolveRowPhysicalStock(row: WarehouseRowWithBatches): number {
  const qty = Number(row.stock ?? 0);
  return Number.isFinite(qty) && qty > 0 ? qty : 0;
}

/** Barcha aktiv partiyalar qoldiq summasi (faqat omborda qoldiq > 0 bo‘lsa). */
export function getBatchStockValueTotal(batches: unknown): number | null {
  const list = normalizeWarehouseBatches(batches);
  if (list.length === 0) return null;
  const total = list.reduce(
    (s, b) => s + Math.round(b.quantityRemaining * b.purchasePrice),
    0,
  );
  return total > 0 ? total : null;
}

export function resolveFifoPurchasePrice(row: WarehouseRowWithBatches): number | null {
  if (resolveRowPhysicalStock(row) <= 0) return null;
  const fromApi = Number(row.fifoPurchasePrice ?? row.fifo_purchase_price ?? 0);
  if (Number.isFinite(fromApi) && fromApi > 0) return fromApi;
  const fifo = getFifoBatch(row.batches);
  if (fifo && fifo.purchasePrice > 0) return fifo.purchasePrice;
  return null;
}

export function resolveFifoSalePrice(row: WarehouseRowWithBatches): number | null {
  if (resolveRowPhysicalStock(row) <= 0) return null;
  const fromApi = Number(row.fifoSalePrice ?? row.fifo_sale_price ?? 0);
  if (Number.isFinite(fromApi) && fromApi > 0) return fromApi;
  const fifo = getFifoBatch(row.batches);
  if (fifo && fifo.salePrice > 0) return fifo.salePrice;
  return null;
}

export function resolvePurchasePriceForEdit(
  sourceRow: WarehouseRowWithBatches,
  detail?: { purchasePrice?: number } | null,
): number {
  const fifo = resolveFifoPurchasePrice(sourceRow);
  if (fifo != null && fifo > 0) return Math.round(fifo);
  const overview = Number(sourceRow.purchasePrice ?? 0);
  if (Number.isFinite(overview) && overview > 0) return Math.round(overview);
  const api = Number(detail?.purchasePrice ?? 0);
  if (Number.isFinite(api) && api > 0) return Math.round(api);
  return 0;
}

export function resolveBatchStockValueTotal(row: WarehouseRowWithBatches): number | null {
  if (resolveRowPhysicalStock(row) <= 0) return null;
  const fromApi = Number(row.batchStockValueTotal ?? row.batch_stock_value_total ?? 0);
  if (Number.isFinite(fromApi) && fromApi > 0) return Math.round(fromApi);
  return getBatchStockValueTotal(row.batches);
}

/** Jadval/KPI: qoldiq × tannarx (0 qoldiq → 0 so‘m). */
export function resolveRowStockValueUzs(row: WarehouseRowWithBatches): number {
  const stock = resolveRowPhysicalStock(row);
  if (stock <= 0) return 0;
  const batchValue = resolveBatchStockValueTotal(row);
  if (batchValue != null && batchValue > 0) return batchValue;
  const buy = Number(row.purchasePrice ?? 0);
  if (Number.isFinite(buy) && buy > 0) return Math.round(stock * buy);
  return 0;
}
